import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { app } from './firebase';
import './SearchResultsPage.css';
import { Heart, ShoppingCart, Bell, Upload, Eye, Settings, CreditCard, RefreshCw, Globe, Play, Pause } from "lucide-react";
import BackButton from "./BackButton";
import EnvironmentManager from '../config/EnvironmentManager';


const NavigationBar = ({ user, onNavigate, currentPage, onSearch }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
  }, [isMobileMenuOpen]);

  const handleNavClick = (page) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      if (page === 'home') {
        navigate('/MHMV-profile/audio-courses/admin');
      } else if (page === 'manage-courses') {
        navigate('/MHMV-profile/audio-courses/admin');
      } else if (page === 'login') {
        navigate('/MHMV-profile/jobseeker-login');
      } else if (page === 'signup') {
        navigate('/MHMV-profile/modal-register');
      }
    }
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (localSearchQuery.trim()) {
      
      navigate('/MHMV-profile/search-results', { 
        state: { keyword: localSearchQuery } 
      });
      setIsMobileMenuOpen(false);
      setIsUserMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth(app);
      await signOut(auth);
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <nav className="professional-nav-bar">
      <div className="nav-container">
        <div className="nav-left">
          <div className="nav-brand" onClick={() => handleNavClick('home')}>
            <div className="logo">
              <span className="logo-text">MHMV</span>
            </div>
          </div>
        </div>

        {/* Center: Search Bar */}
        <div className="nav-center">
          <form className="nav-search-bar" onSubmit={handleSearchSubmit}>
            <div className="search-input-wrapper">
              <span className="search-icon"></span>
              <input
                type="text"
                className="search-input"
                placeholder="Search for courses"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
              />
              {localSearchQuery && (
                <button 
                  type="button" 
                  className="clear-search"
                  onClick={() => setLocalSearchQuery('')}
                >
                  ✕
                </button>
              )}
            </div>
            <button type="submit" className="search-submit-btn">
              Search
            </button>
          </form>
        </div>

        <div className="nav-right">
          {/* Navigation Tabs */}
          <div className="nav-tabs">
            <button 
              className={`nav-tab ${currentPage === 'home' ? 'active' : ''}`}
              onClick={() => handleNavClick('home')}
            >
              Home
            </button>
            <button 
              className={`nav-tab ${currentPage === 'manage-courses' ? 'active' : ''}`}
              onClick={() => handleNavClick('manage-courses')}
            >
              Manage Courses
            </button>
          </div>

          <div className="nav-icons">
            <button className="nav-icon-btn" title="Wishlist">
              <Heart size={18} />
            </button>
            <button className="nav-icon-btn" title="Cart">
              <ShoppingCart size={20} />
              <span className="icon-badge">1</span>
            </button>
            <button className="nav-icon-btn" title="Notifications">
              <Bell size={20} />
              <span className="icon-badge">2</span>
            </button>

            {/* User Menu */}
            {user ? (
              <div className="user-menu-container">
                <button 
                  className="user-avatar-btn"
                  onClick={handleUserMenuToggle}
                >
                  <div className="user-avatar-small">
                    {user.email ? user.email[0].toUpperCase() : 'U'}
                  </div>
                </button>
                
                {isUserMenuOpen && (
                  <div className="user-dropdown-menu">
                    <div className="dropdown-header">
                      <div className="user-info-section">
                        <div className="user-avatar-large">
                          {user.email ? user.email[0].toUpperCase() : 'U'}
                        </div>
                        <div className="user-details">
                          <div className="user-name">
                            {user.displayName || user.email?.split('@')[0]}
                          </div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="dropdown-section">
                      <button 
                        className="dropdown-item"
                        onClick={() => handleNavClick('upload')}
                      >
                        <Upload size={18} />
                        Upload Courses
                      </button>
                      <button 
                        className="dropdown-item"
                        onClick={() => handleNavClick('courses')}
                      >
                        <Eye size={18} />
                        View Courses
                      </button>
                      <button 
                        className="dropdown-item"
                        onClick={() => handleNavClick('archive')}
                      >
                        <Eye size={18} />
                        Archived Courses
                      </button>
                    </div>

                    <div className="dropdown-section">
                      <button className="dropdown-item">
                        <Heart size={18} />
                        Wishlist
                      </button>
                      <button className="dropdown-item">
                        <ShoppingCart size={20} />
                        Cart
                      </button>
                    </div>

                    <div className="dropdown-section">
                      <button className="dropdown-item">
                        <Settings size={18} />
                        Account Settings
                      </button>
                      <button className="dropdown-item">
                        <CreditCard size={18} />
                        Payment Methods
                      </button>
                      <button className="dropdown-item">
                        <RefreshCw size={18} />
                        Subscriptions
                      </button>
                    </div>

                    <div className="dropdown-section">
                      <button className="dropdown-item">
                        <Globe size={18} />
                        Language: English
                      </button>
                      <button 
                        className="dropdown-item logout"
                        onClick={handleLogout}
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons">
                <button 
                  className="login-btn"
                  onClick={() => handleNavClick('login')}
                >
                  Log in
                </button>
                <button 
                  className="signup-btn"
                  onClick={() => handleNavClick('signup')}
                >
                  Sign up
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={handleMobileMenuToggle}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={handleMobileMenuToggle}></div>
        )}

        <div className={`udemy-mobile-menu ${isMobileMenuOpen ? "active" : ""}`}>
          <div className="udemy-mobile-header">
            <span className="udemy-menu-title">Menu</span>
            <button className="udemy-close-btn" onClick={handleMobileMenuToggle}>×</button>
          </div>

          <div className="udemy-user-section">
            {user ? (
              <>
                <div className="udemy-user-info">
                  <div className="udemy-user-avatar">
                    {user.email ? user.email[0].toUpperCase() : "U"}
                  </div>
                  <div className="udemy-user-details">
                    <div className="udemy-user-name">{user.displayName || user.email?.split("@")[0]}</div>
                    <div className="udemy-user-email">{user.email}</div>
                  </div>
                </div>

                <div className="udemy-menu-links">
                  <button onClick={() => handleNavClick("home")}>Home</button>
                  <button onClick={() => handleNavClick("manage-courses")}>Manage Courses</button>
                  <button onClick={() => handleNavClick("upload")}>Upload Courses</button>
                  <button onClick={() => handleNavClick("courses")}>View Courses</button>
                  <button onClick={() => handleNavClick("archive")}>Archived Courses</button>
                  <button>Wishlist</button>
                  <button>Cart</button>
                  <button>Account Settings</button>
                  <button>Payment Methods</button>
                  <button>Subscriptions</button>
                  <button>Language: English</button>
                  <button className="udemy-signout" onClick={handleLogout}>Sign Out</button>
                </div>
              </>
            ) : (
              <div className="udemy-auth-links">
                <button onClick={() => handleNavClick("login")}>Log In</button>
                <button onClick={() => handleNavClick("signup")}>Sign Up</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Menu Overlay */}
      {isUserMenuOpen && (
        <div className="user-menu-overlay" onClick={() => setIsUserMenuOpen(false)}></div>
      )}
    </nav>
  );
};


const CoursesGridView = ({ courses, onCourseSelect, showOptions = false }) => {
  return (
    <div className="courses-grid-view-full udemy-grid">
      <div className="courses-grid-full udemy-grid-inner">
        {courses.map((course, index) => (
          <div
            key={course.courseId || index}
            className="course-card-full udemy-card"
            onClick={() => onCourseSelect && onCourseSelect(course)}
          >
            <div className="course-card-header-full udemy-thumb">
              <div className="course-image-full">
                <div className="course-image-placeholder">
                  {course.title?.[0]?.toUpperCase() || "C"}
                </div>
              </div>
              <div className="course-badge-full">Bestseller</div>
            </div>

            <div className="course-card-body-full udemy-body">
              <h3 className="course-card-title-full udemy-title">
                {course.title || `Course ${index + 1}`}
              </h3>
              {course.instructor && (
                <p className="course-instructor-full udemy-instructor">
                  By {course.instructor}
                </p>
              )}
              <div className="udemy-meta-row">
                {course.price && <span className="udemy-price">{course.price}</span>}
              </div>
            </div>

            <div className="course-card-footer-full udemy-footer">
              <button className="view-course-button-full" onClick={(e) => {
                e.stopPropagation();
                onCourseSelect && onCourseSelect(course);
              }}>
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const CourseDetailView = ({ course, onBack, user }) => {
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (index) => {
    setExpandedModules((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="course-detail-view-full">
      <div className="view-section-full">
        <div className="view-header-full">
          <div className="view-title-section">
            <h2>Course Details</h2>
            <p>{course?.title || 'Course'}</p>
          </div>
          <div className="view-actions-full">
            <button onClick={onBack} className="back-button-full">
              ← Back
            </button>
          </div>
        </div>

        <div className="course-overview-full">
          <div className="course-header-full">
            <h1 className="course-title-full">{course?.title || 'Course Title'}</h1>
            <div className="course-meta-full">
              {course?.category && <span className="meta-tag-full category">{course.category}</span>}
              {course?.language && <span className="meta-tag-full language">{course.language}</span>}
              {course?.level && <span className="meta-tag-full level">{course.level}</span>}
            </div>
          </div>

          {course?.description && (
            <div className="course-description-full">
              <p>{course.description}</p>
            </div>
          )}
        </div>

        {course?.modules && course.modules.length > 0 && (
          <div className="modules-section-full">
            <h2 className="section-title-full">Course Content</h2>
            <div className="modules-list-full">
              {course.modules.map((module, moduleIndex) => (
                <div key={moduleIndex} className="module-item-full">
                  <div className="module-header-full" onClick={() => toggleModule(moduleIndex)}>
                    <div className="module-title-section-full">
                      <span className="expand-icon-full">
                        {expandedModules[moduleIndex] ? "▼" : "▶"}
                      </span>
                      <div className="module-info-full">
                        <h3 className="module-title-full">
                          Module {moduleIndex + 1}: {module.title || `Module ${moduleIndex + 1}`}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  
  const initialKeyword = location.state?.keyword || '';
  
  // State for search parameters
  const [searchParams, setSearchParams] = useState({
    keyword: initialKeyword,
    language: '',
    category: '',
    level: ''
  });
  
  // Filter options
  const [filterOptions] = useState({
    languages: ['Hindi', 'English', 'Spanish', 'French', 'German', 'Telugu', 'Tamil'],
    categories: ['Grammar', 'Vocabulary', 'Communication', 'Business', 'Academic', 'Technical'],
    levels: ['Beginner', 'Intermediate', 'Advance', 'Expert']
  });
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(8);

  // Check authentication
  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (initialKeyword) {
          fetchSearchResults({ keyword: initialKeyword }, currentUser);
        }
      } else {
        navigate('/MHMV-profile/jobseeker-login');
      }
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, [initialKeyword, navigate]);

  // Fetch search results 
  const fetchSearchResults = async (params, currentUser = user) => {
    if (!currentUser) return;
    
    setLoading(true);
    setError('');
    
    try {
      const idToken = await currentUser.getIdToken();
      
      const payload = {
        keyword: params.keyword || '',
        language: params.language || '',
        category: params.category || '',
        level: params.level || ''
      };
      
      const response = await axios.post(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.searchCourses}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: EnvironmentManager.config.requestTimeout
        }
      );
      
      setResults(response.data || []);
      
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.message || 'Failed to fetch search results');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchParams.keyword.trim()) {
      fetchSearchResults(searchParams);
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (field, value) => {
    const newParams = {
      ...searchParams,
      [field]: value
    };
    setSearchParams(newParams);
    
    if (searchParams.keyword || value) {
      fetchSearchResults(newParams);
    }
  };
  
  // Apply filters
  const handleApplyFilters = () => {
    fetchSearchResults(searchParams);
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    const clearedParams = {
      keyword: initialKeyword,
      language: '',
      category: '',
      level: ''
    };
    setSearchParams(clearedParams);
    
    if (initialKeyword) {
      fetchSearchResults(clearedParams);
    } else {
      setResults([]);
    }
  };

  // Pagination
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = results.slice(indexOfFirstCourse, indexOfLastCourse);
  const totalPages = Math.ceil(results.length / coursesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Handle course selection
  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setViewMode('detail');
  };
  
  const handleBackToGrid = () => {
    setViewMode('grid');
    setSelectedCourse(null);
  };

  // Handle navigation
  const handleNavigation = (page) => {
    if (page === 'login') {
      navigate('/MHMV-profile/jobseeker-login');
    } else if (page === 'signup') {
      navigate('/MHMV-profile/modal-register');
    } else if (page === 'manage-courses') {
      navigate('/MHMV-profile/audio-courses/admin');
    } else {
      navigate('/MHMV-profile/audio-courses/admin');
    }
  };

  // Loading state
  if (loadingAuth) {
    return (
      <div className="srp-loading-auth">
        <div className="srp-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Course detail view
  if (viewMode === 'detail' && selectedCourse) {
    return (
      <div className="search-results-page">
        <NavigationBar 
          user={user} 
          onNavigate={handleNavigation} 
          currentPage="search" 
        />
        <div className="srp-main-content">
          <CourseDetailView 
            course={selectedCourse} 
            onBack={handleBackToGrid}
            user={user}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="search-results-page">
      <NavigationBar 
        user={user} 
        onNavigate={handleNavigation} 
        currentPage="search" 
      />
      
      {/* Search Header */}
      
      
      <div className="srp-results-container">
        {/* Filter Sidebar */}
        <div className={`srp-filter-sidebar ${showFilters ? 'open' : 'closed'}`}>
          <div className="srp-filter-header">
            <h2>Filters</h2>
            <button 
              className="srp-toggle-filters-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {showFilters && (
            <>
              <div className="srp-filter-group">
                <label className="srp-filter-label">Language</label>
                <select 
                  value={searchParams.language}
                  onChange={(e) => handleFilterChange('language', e.target.value)}
                  className="srp-filter-select"
                >
                  <option value="">All Languages</option>
                  {filterOptions.languages.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
              
              <div className="srp-filter-group">
                <label className="srp-filter-label">Category</label>
                <select 
                  value={searchParams.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="srp-filter-select"
                >
                  <option value="">All Categories</option>
                  {filterOptions.categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="srp-filter-group">
                <label className="srp-filter-label">Level</label>
                <select 
                  value={searchParams.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                  className="srp-filter-select"
                >
                  <option value="">All Levels</option>
                  {filterOptions.levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              
              <div className="srp-filter-actions">
                <button 
                  onClick={handleApplyFilters}
                  className="srp-apply-filters-btn"
                  disabled={loading}
                >
                  Apply Filters
                </button>
                <button 
                  onClick={handleClearFilters}
                  className="srp-clear-filters-btn"
                  disabled={loading}
                >
                  Clear Filters
                </button>
              </div>
            </>
          )}
        </div>
        
        {/* Results Main Area */}
        <div className="srp-results-main">
          <div className="srp-results-header">
            <div className="srp-results-title">
              <h2>Search Results</h2>
              <div className="srp-results-stats">
                <span className="srp-results-count">
                  {loading ? 'Searching...' : `${results.length} courses found`}
                </span>
                {initialKeyword && (
                  <span className="srp-search-keyword">for "{initialKeyword}"</span>
                )}
              </div>
            </div>
            
            {(searchParams.language || searchParams.category || searchParams.level) && (
              <div className="srp-active-filters-display">
                <span className="srp-active-filters-label">Active filters:</span>
                {searchParams.language && (
                  <span className="srp-active-filter-tag">Language: {searchParams.language}</span>
                )}
                {searchParams.category && (
                  <span className="srp-active-filter-tag">Category: {searchParams.category}</span>
                )}
                {searchParams.level && (
                  <span className="srp-active-filter-tag">Level: {searchParams.level}</span>
                )}
                <button 
                  onClick={handleClearFilters}
                  className="srp-clear-active-filters"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
          
          {error && (
            <div className="srp-error-message">
              <span className="srp-error-icon">⚠️</span>
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="srp-loading-results">
              <div className="srp-spinner"></div>
              <p>Searching for courses...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="srp-no-results">
              <div className="srp-no-results-icon">🔍</div>
              <h3>No courses found</h3>
              <p>
                {searchParams.keyword || searchParams.language || searchParams.category || searchParams.level
                  ? 'Try different search terms or adjust your filters'
                  : 'Enter a search term to find courses'
                }
              </p>
            </div>
          ) : (
            <>
              <CoursesGridView 
                courses={currentCourses} 
                onCourseSelect={handleCourseSelect}
                showOptions={false}
              />
              
              {totalPages > 1 && (
                <div className="srp-pagination">
                  <button 
                    className="srp-pagination-btn"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  
                  <div className="srp-pagination-numbers">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          className={`srp-pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => paginate(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <span className="srp-pagination-dots">...</span>
                        <button
                          className="srp-pagination-number"
                          onClick={() => paginate(totalPages)}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>
                  
                  <button 
                    className="srp-pagination-btn"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
              
              <div className="srp-results-summary">
                <p>
                  Showing {indexOfFirstCourse + 1} to {Math.min(indexOfLastCourse, results.length)} of {results.length} results
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;